import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useSchool } from "@/context/SchoolContext";
import { initials, fmt } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Award, FileText, Loader2, Search, Settings2, Save, Plus, Trash2, Eye, ChevronUp, ChevronDown, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { UseTemplateDialog } from "@/components/UseTemplateDialog";

const SAMPLE = { name: "Krishna Yadav", father: "Dr. Ranjeet Singh", mother: "Sunita Devi", class: "3rd", section: "A", roll: "5", admission_number: "1", dob: "01-04-2016", gender: "Male", blood_group: "B+", height: "120 cm", weight: "25 kg", admission_date: "01-04-2022", session: "2025-26", phone: "9876543210", whatsapp: "9876543210", alt_phone: "9876500000", address: "12 Gandhi Road", city: "Lucknow", state: "Uttar Pradesh", pin_code: "226001", aadhar_number: "XXXX-XXXX-1234", apaar_id: "APAAR12345", admission_class: "Nursery", admission_type: "New", caste: "General", religion: "Hindu", category: "General", nationality: "Indian", house: "Blue" };
const fillTpl = (t) => (t || "").replace(/\{(\w+)\}/g, (_, k) => (SAMPLE[k] !== undefined ? SAMPLE[k] : `{${k}}`));
const DEFAULT_TPL = {
  transfer: { title: "TRANSFER CERTIFICATE", align: "left", body: "This is to certify that {name}, son/daughter of {father} and {mother},\nwas a bonafide student of this school.\n\nAdmission Number: {admission_number}\nClass: {class}    Section: {section}\nDate of Birth: {dob}\nDate of Admission: {admission_date}\n\nThe student has no dues pending and bears a good moral character.\nThis certificate is issued on request for the purpose of transfer." },
  character: { title: "CHARACTER CERTIFICATE", align: "center", body: "This is to certify that {name},\nson/daughter of {father},\nwas a student of class {class} (Section {section})\nbearing Admission Number {admission_number}.\n\nTo the best of our knowledge, he/she bears a good moral\ncharacter and was not involved in any act of indiscipline\nduring his/her stay in the school.\n\nWe wish him/her success in all future endeavours." },
  bonafide: { title: "BONAFIDE CERTIFICATE", align: "center", body: "This is to certify that {name},\nS/o D/o {father},\nis a bonafide student of class {class} (Section {section})\nbearing Admission Number {admission_number}\nduring the academic session {session}." },
  other: { title: "CERTIFICATE", align: "left", body: "Awarded to {name} of class {class} (Section {section})." },
};
const CS_DEFAULTS = { show_header: true, show_border: true, show_date: true, show_signatory: true, title_size: 16, body_size: 12, orientation: "portrait", show_logo: false, logo_size: 22, logo_align: "center", copy_type: "none", serial_show: "hide", serial_type: "automatic", serial_label: "Se. No.", serial_start: 1, allow_due_action: false, margin_top: 20, margin_right: 30, margin_bottom: 20, margin_left: 30, watermark: false, watermark_type: "logo", watermark_text: "", watermark_opacity: 0.08, watermark_size: 120, auto_arrange: true };
const PLACEHOLDERS = ["name", "father", "mother", "class", "section", "roll", "admission_number", "dob", "gender", "blood_group", "height", "weight", "admission_date", "session", "phone", "whatsapp", "alt_phone", "address", "city", "state", "pin_code", "aadhar_number", "pen_number", "apaar_id", "admission_class", "admission_type", "caste", "religion", "category", "nationality", "house"];
const FONTS = [{ v: "Helvetica", label: "Sans" }, { v: "Times-Roman", label: "Serif" }, { v: "Courier", label: "Mono" }];
const FONT_CSS = { "Helvetica": "Helvetica, Arial, sans-serif", "Times-Roman": "Georgia, 'Times New Roman', serif", "Courier": "'Courier New', monospace" };
const FIELD_LABELS = { name: "Name", father: "Father's Name", mother: "Mother's Name", class: "Class", section: "Section", roll: "Roll No.", admission_number: "Admission No.", dob: "Date of Birth", gender: "Gender", blood_group: "Blood Group", height: "Height", weight: "Weight", admission_date: "Admission Date", session: "Session", phone: "Phone", whatsapp: "WhatsApp", alt_phone: "Alt. Phone", address: "Address", city: "City", state: "State", pin_code: "PIN Code", aadhar_number: "Aadhar No.", apaar_id: "APAAR ID", pen_number: "PEN No.", admission_class: "Admission Class", admission_type: "Admission Type", caste: "Caste", religion: "Religion", category: "Category", nationality: "Nationality", house: "House" };

const TYPES = {
  transfer: { label: "Transfer Certificate", desc: "Issue a Transfer Certificate (TC) for a student leaving the school." },
  character: { label: "Character Certificate", desc: "Certify a student's good moral character." },
  bonafide: { label: "Bonafide Certificate", desc: "Certify that a student is a bonafide student of the school." },
  other: { label: "Other Certificates", desc: "Create a custom certificate with your own title and text." },
};

export default function Certificates() {
  const { type } = useParams();
  const ctype = TYPES[type] ? type : "transfer";
  const meta = TYPES[ctype];
  const { school, refreshSchool } = useSchool();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [cs, setCs] = useState(CS_DEFAULTS);
  const [savingCs, setSavingCs] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("all");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [serialNo, setSerialNo] = useState("");
  const certCfg = (school?.certificate_settings || {})[ctype] || {};
  const [previewScale, setPreviewScale] = useState(1);
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY, startScale = previewScale;
    const base = cs.orientation === "landscape" ? 540 : 382;
    const onMove = (ev) => {
      const delta = Math.max(ev.clientX - startX, ev.clientY - startY);
      setPreviewScale(Math.max(0.5, Math.min(1.8, startScale + delta / base)));
    };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const [prevUrl, setPrevUrl] = useState("");
  const [prevLoading, setPrevLoading] = useState(false);
  const [resizeMode, setResizeMode] = useState(false);
  const refreshPreview = async () => {
    const sample = students[0];
    if (!sample) return;
    setPrevLoading(true);
    try {
      const r = await api.get(`/students/${sample.id}/certificate-pdf`, { params: { ctype, title, body: bodyText, preview: 1, cs_override: JSON.stringify(cs), _t: Date.now() }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPrevUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (e) { /* ignore preview errors */ }
    finally { setPrevLoading(false); }
  };
  useEffect(() => {
    if (!settingsOpen) return;
    const t = setTimeout(refreshPreview, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [cs, settingsOpen, title, bodyText, students.length]);

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);
  const classNames = [...new Set(classes.map((c) => (c.name || "").trim()).filter(Boolean))];

  useEffect(() => {
    setLoading(true);
    if (ctype === "transfer") {
      api.get("/students/deleted").then((r) => setStudents(r.data || [])).catch(() => setStudents([])).finally(() => setLoading(false));
    } else {
      const params = { limit: 1000 };
      if (classId !== "all") params.class_name = classId;
      api.get("/students", { params }).then((r) => setStudents(r.data.students || [])).catch(() => setStudents([])).finally(() => setLoading(false));
    }
  }, [classId, ctype]);

  const filtered = students.filter((s) => {
    if (ctype === "transfer" && classId !== "all" && (s.class_name || "").trim() !== classId) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [s.name, s.admission_number, s.roll_number, s.class_name].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const isEnabled = (s) => {
    if (ctype !== "transfer") return true;
    if ((s.fees_summary?.due || 0) <= 0) return true;
    return certCfg.allow_due_action === true;
  };
  const [fmtTarget, setFmtTarget] = useState(null);
  const doGenerate = async (s, preview = false, fileFmt = "pdf") => {
    if (ctype === "other" && !title.trim()) { toast.error("Enter a certificate title first"); return; }
    if (ctype === "transfer" && !isEnabled(s)) { toast.error(`${s.name} has pending dues. Enable "action when due" in Certificate Settings to issue.`); return; }
    setBusyId(s.id);
    try {
      const r = await api.get(`/students/${s.id}/certificate-pdf`, { params: { ctype, title, body: bodyText, serial: serialNo, preview: preview ? 1 : 0, fmt: fileFmt, _t: Date.now() }, responseType: "blob" });
      if (fileFmt === "docx") {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `Certificate-${s.admission_number || s.name}.docx`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(URL.createObjectURL(r.data), "_blank");
      }
      toast.success(`${meta.label} ${preview ? "preview" : "generated"} for ${s.name}`);
    } catch (e) { toast.error("Could not generate certificate"); }
    finally { setBusyId(null); }
  };
  const generate = async (s, preview = false) => {
    if (ctype === "transfer" && !preview) {
      if (!isEnabled(s)) { toast.error(`${s.name} has pending dues. Enable "action when due" in Certificate Settings to issue.`); return; }
      setFmtTarget(s);
      return;
    }
    doGenerate(s, preview, "pdf");
  };

  const [selected, setSelected] = useState(() => new Set());
  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(() => allChecked ? new Set() : new Set(filtered.map((s) => s.id)));

  const setCsField = (k, v) => setCs((c) => ({ ...c, [k]: v }));
  const openSettings = () => {
    const saved = (school?.certificate_settings || {})[ctype] || {};
    const headerDefaults = {
      header_lines: [
        { text: school?.school_name || "SSSPS", align: "center", size: 18, font: "Helvetica", bold: true, spacing: 1 },
        { text: school?.tag_line || "", align: "center", size: 9, font: "Helvetica", bold: false, spacing: 1 },
        { text: [school?.address, school?.city, school?.state, school?.pin_code].filter(Boolean).join(", "), align: "center", size: 9, font: "Helvetica", bold: false, spacing: 1 },
        { text: `Phone: ${school?.school_number || school?.phone || ""}  Email: ${school?.email || ""}`, align: "center", size: 9, font: "Helvetica", bold: false, spacing: 1 },
      ].filter((l) => l.text),
      title_font: "Helvetica", body_font: "Helvetica", line_spacing: 1,
      body_lines: (DEFAULT_TPL[ctype].body || "").split("\n").map((t) => ({ text: t, align: DEFAULT_TPL[ctype].align || "left", size: 12, font: "Helvetica", bold: false, spacing: 1 })),
      signatory: school?.authorized_signatory || "Principal",
      date_label: "Date:",
    };
    setCs({ ...CS_DEFAULTS, ...headerDefaults, ...DEFAULT_TPL[ctype], ...saved });
    setSettingsOpen(true);
  };
  const setHeaderLine = (i, k, v) => setCs((c) => ({ ...c, header_lines: c.header_lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const addHeaderLine = () => setCs((c) => ({ ...c, header_lines: [...(c.header_lines || []), { text: "", align: "center", size: 10, font: "Helvetica", bold: false, spacing: 1 }] }));
  const removeHeaderLine = (i) => setCs((c) => ({ ...c, header_lines: c.header_lines.filter((_, idx) => idx !== i) }));
  const setBodyLine = (i, k, v) => setCs((c) => ({ ...c, body_lines: c.body_lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const addTextLine = (section = "lower") => setCs((c) => ({ ...c, body_lines: [...(c.body_lines || []), { kind: "text", text: "", align: "left", size: 12, font: "Helvetica", bold: false, spacing: 1, flow: "new", section }] }));
  const addFieldLine = (section = "lower") => setCs((c) => ({ ...c, body_lines: [...(c.body_lines || []), { kind: "field", label: "", value: "{name}", align: "left", size: 12, font: "Helvetica", bold: false, spacing: 1, flow: "new", section }] }));
  const removeBodyLine = (i) => setCs((c) => ({ ...c, body_lines: c.body_lines.filter((_, idx) => idx !== i) }));
  const moveLine = (i, dir) => setCs((c) => {
    const arr = [...(c.body_lines || [])];
    const sec = arr[i].section || "lower";
    let j = i + dir;
    while (j >= 0 && j < arr.length && (arr[j].section || "lower") !== sec) j += dir;
    if (j < 0 || j >= arr.length) return c;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...c, body_lines: arr };
  });
  const insertBodyField = (i, key) => { if (!key) return; setCs((c) => ({ ...c, body_lines: c.body_lines.map((l, idx) => idx === i ? { ...l, text: (l.text || "") + `{${key}}` } : l) })); };
  const renderBodyRow = (l, i) => {
    const usedKeys = new Set();
    (cs.body_lines || []).forEach((bl, idx) => {
      if (idx === i) return;
      if (bl.kind === "field" && bl.value) { const m = bl.value.match(/\{(\w+)\}/); if (m) usedKeys.add(m[1]); }
      else if (bl.text) { (bl.text.match(/\{(\w+)\}/g) || []).forEach((x) => usedKeys.add(x.slice(1, -1))); }
    });
    const curKey = (l.value || "").replace(/[{}]/g, "");
    const availValue = PLACEHOLDERS.filter((p) => !usedKeys.has(p) || p === curKey);
    const availInsert = PLACEHOLDERS.filter((p) => !usedKeys.has(p));
    return (
    <div key={i} className="flex flex-wrap items-center gap-1.5 bg-slate-50 rounded-lg border border-slate-200 p-2 min-w-0" data-testid={`cs-body-line-${i}`}>
      {l.kind === "field" ? (
        <>
          <Input className="flex-1 min-w-[70px] h-8" value={l.label || ""} onChange={(e) => setBodyLine(i, "label", e.target.value)} placeholder="Label (e.g. Name)" data-testid={`cs-body-label-${i}`} />
          <span className="text-slate-400 text-sm font-semibold">:</span>
          <select className="flex-1 min-w-[80px] h-8 rounded border border-slate-200 text-xs px-1 bg-white" value={l.value || ""} onChange={(e) => setBodyLine(i, "value", e.target.value)} data-testid={`cs-body-value-${i}`}>
            {availValue.map((p) => <option key={p} value={`{${p}}`}>{FIELD_LABELS[p] || p}</option>)}
          </select>
        </>
      ) : (
        <>
          <Input className="flex-1 min-w-[90px] h-8" value={l.text} onChange={(e) => setBodyLine(i, "text", e.target.value)} placeholder="Text or {field}" data-testid={`cs-body-text-${i}`} />
          <select className="h-8 rounded border border-slate-200 text-xs px-1 bg-white" value="" onChange={(e) => insertBodyField(i, e.target.value)} data-testid={`cs-body-field-${i}`}>
            <option value="">+ Field</option>
            {availInsert.map((p) => <option key={p} value={p}>{FIELD_LABELS[p] || p}</option>)}
          </select>
          <select className="h-8 rounded border border-slate-200 text-xs px-1 bg-white" value={l.align} onChange={(e) => setBodyLine(i, "align", e.target.value)} data-testid={`cs-body-lalign-${i}`}>
            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
          </select>
        </>
      )}
      <select className="h-8 rounded border border-slate-200 text-xs px-1 bg-white" value={l.font} onChange={(e) => setBodyLine(i, "font", e.target.value)} data-testid={`cs-body-font-${i}`}>
        {FONTS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
      </select>
      <select
        className="h-8 rounded border border-slate-200 text-xs px-1 bg-white"
        value={l.flow || "new"}
        onChange={(e) => {
          const val = e.target.value;
          setCs((c) => ({
            ...c,
            body_lines: c.body_lines.map((line, idx) => {
              if (idx !== i) return line;
              const isUpper = (line.section || "lower") === "upper";
              return { ...line, flow: val, align: val === "same" && isUpper ? "right" : line.align };
            }),
          }));
        }}
        title="Text in line"
        data-testid={`cs-body-flow-${i}`}
      >
        <option value="new">New Line</option>
        <option value="same">Same Line</option>
        {l.kind === "field" && l.value === "{admission_number}" && <option value="serial">Se. No. Line</option>}
      </select>
      <select className="h-8 rounded border border-slate-200 text-xs px-1 bg-white" value={l.section || "lower"} onChange={(e) => setBodyLine(i, "section", e.target.value)} title="Section" data-testid={`cs-body-section-${i}`}>
        <option value="upper">Upper</option>
        <option value="lower">Lower</option>
      </select>
      <Input type="number" className="w-12 h-8" title="Font size" value={l.size} onChange={(e) => setBodyLine(i, "size", parseFloat(e.target.value) || 12)} data-testid={`cs-body-size-${i}`} />
      <Input type="number" step="0.1" className="w-12 h-8" title="Line spacing" value={l.spacing} onChange={(e) => setBodyLine(i, "spacing", parseFloat(e.target.value) || 1)} data-testid={`cs-body-spacing-${i}`} />
      <button type="button" onClick={() => setBodyLine(i, "bold", !l.bold)} title="Heading / Bold" className={`h-8 w-8 rounded border text-sm font-bold ${l.bold ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "border-slate-200 text-slate-500"}`} data-testid={`cs-body-bold-${i}`}>H</button>
      <div className="flex flex-col">
        <button type="button" onClick={() => moveLine(i, -1)} title="Move up" className="h-4 w-6 rounded text-slate-400 hover:text-indigo-600 hover:bg-slate-100 flex items-center justify-center" data-testid={`cs-body-up-${i}`}><ChevronUp size={13} /></button>
        <button type="button" onClick={() => moveLine(i, 1)} title="Move down" className="h-4 w-6 rounded text-slate-400 hover:text-indigo-600 hover:bg-slate-100 flex items-center justify-center" data-testid={`cs-body-down-${i}`}><ChevronDown size={13} /></button>
      </div>
      <button type="button" onClick={() => removeBodyLine(i)} className="h-8 w-8 rounded text-rose-500 hover:bg-rose-50 flex items-center justify-center" data-testid={`cs-body-remove-${i}`}><Trash2 size={14} /></button>
    </div>
  );
  };
  const saveSettings = async () => {
    setSavingCs(true);
    try {
      const next = { ...(school?.certificate_settings || {}), [ctype]: cs };
      await api.put("/settings", { ...school, certificate_settings: next });
      await refreshSchool();
      toast.success("Certificate settings saved");
      setSettingsOpen(false);
    } catch (e) { toast.error("Could not save settings"); }
    finally { setSavingCs(false); }
  };

  const rz = resizeMode ? "resize overflow-auto min-h-[80px] min-w-[220px] outline-dashed outline-1 outline-indigo-300" : "";
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Award className="text-indigo-500" size={26} />{meta.label}</h1>
          <p className="text-slate-500">{meta.desc}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => { if (!filtered.length) { toast.error("No students available"); return; } setTplOpen(true); }} data-testid="cert-use-template-btn"><LayoutTemplate size={16} />Use Ready Template</Button>
          <Button variant="outline" className="gap-1.5" onClick={openSettings} data-testid="certificate-settings-btn"><Settings2 size={16} />Certificate Settings</Button>
        </div>
      </div>

      {ctype === "other" && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="cert-other-fields">
          <div>
            <Label>Certificate Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sports Achievement Certificate" data-testid="cert-title-input" />
          </div>
          <div>
            <Label>Certificate Text</Label>
            <Textarea rows={5} value={bodyText} onChange={(e) => setBodyText(e.target.value)} placeholder="Write the certificate body. Student details can be typed here as needed." data-testid="cert-body-input" />
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-56" data-testid="cert-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, admission no, roll…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cert-search" />
        </div>
        {certCfg.serial_show === "show" && certCfg.serial_type === "manual" && (
          <div className="flex items-center gap-1.5">
            <Label className="mb-0 text-xs whitespace-nowrap">{certCfg.serial_label || "Se. No."}</Label>
            <Input className="w-32 h-9" placeholder="Serial no." value={serialNo} onChange={(e) => setSerialNo(e.target.value)} data-testid="cert-serial-input" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          {selected.size > 0 && (
            <Button size="sm" className="gap-1.5" onClick={async () => { for (const s of filtered.filter((x) => selected.has(x.id))) { await generate(s); } }} data-testid="cert-generate-selected"><FileText size={14} />Generate Selected ({selected.size})</Button>
          )}
          <span className="text-xs text-slate-400">{filtered.length} of {students.length}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><Award size={40} className="mx-auto mb-2 opacity-40" />No students found.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 w-10"><input type="checkbox" className="h-4 w-4 accent-indigo-600 cursor-pointer" checked={allChecked} onChange={toggleAll} data-testid="cert-select-all" /></th>
                  <th className="text-left px-4 py-3">Photo</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Admission No.</th>
                  <th className="text-left px-4 py-3">Class</th>
                  {ctype === "transfer" && <th className="text-left px-4 py-3">Due at Left</th>}
                  {ctype === "transfer" && <th className="text-left px-4 py-3">TC Status</th>}
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`cert-row-${s.id}`}>
                    <td className="px-4 py-2.5"><input type="checkbox" className="h-4 w-4 accent-indigo-600 cursor-pointer" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)} data-testid={`cert-select-${s.id}`} /></td>
                    <td className="px-4 py-2.5">
                      {s.docs && s.docs.photo ? <img src={s.docs.photo} alt={s.name} className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">{initials(s.name)}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-800">{s.name}</div>
                      {s.father_name && <div className="text-xs text-slate-400">S/o {s.father_name}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{s.admission_number}</td>
                    <td className="px-4 py-2.5 text-slate-500">{s.class_name}</td>
                    {ctype === "transfer" && <td className="px-4 py-2.5"><span className={`font-medium tabular ${(s.fees_summary?.due || 0) > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(s.fees_summary?.due || 0)}</span></td>}
                    {ctype === "transfer" && <td className="px-4 py-2.5 text-slate-500">{s.tc_status || "—"}</td>}
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="gap-1.5" disabled={busyId === s.id || !isEnabled(s)} onClick={() => generate(s, true)} data-testid={`cert-preview-${s.id}`}><Eye size={14} />Preview</Button>
                        <Button size="sm" variant="outline" className="gap-1.5" disabled={busyId === s.id || !isEnabled(s)} onClick={() => generate(s)} data-testid={`cert-generate-${s.id}`}>
                          {busyId === s.id ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}Generate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="certificate-settings-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle>Certificate Settings — {meta.label}</DialogTitle>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer whitespace-nowrap" data-testid="cs-resize-toggle">
                <Switch checked={resizeMode} onCheckedChange={setResizeMode} />
                Adjust height &amp; Width
              </label>
            </div>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5">
            {/* Tools */}
            <div className="space-y-4 min-w-0">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <Label className="mb-0">School Header Fields</Label>
                  <Button size="sm" variant="outline" className="gap-1 h-7" onClick={addHeaderLine} data-testid="cs-header-add"><Plus size={13} />Add Field</Button>
                </div>
                <div className={`space-y-2 ${rz}`}>
                  {(cs.header_lines || []).map((l, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-1.5 bg-white rounded-lg border border-slate-200 p-2" data-testid={`cs-header-line-${i}`}>
                      <Input className="flex-1 min-w-[130px] h-8" value={l.text} onChange={(e) => setHeaderLine(i, "text", e.target.value)} placeholder="Header text" data-testid={`cs-header-text-${i}`} />
                      <select className="h-8 rounded border border-slate-200 text-xs px-1 bg-white" value={l.font} onChange={(e) => setHeaderLine(i, "font", e.target.value)} data-testid={`cs-header-font-${i}`}>
                        {FONTS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                      </select>
                      <select className="h-8 rounded border border-slate-200 text-xs px-1 bg-white" value={l.align} onChange={(e) => setHeaderLine(i, "align", e.target.value)} data-testid={`cs-header-align-${i}`}>
                        <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                      </select>
                      <Input type="number" className="w-14 h-8" title="Font size" value={l.size} onChange={(e) => setHeaderLine(i, "size", parseFloat(e.target.value) || 10)} data-testid={`cs-header-size-${i}`} />
                      <Input type="number" step="0.1" className="w-14 h-8" title="Line spacing" value={l.spacing} onChange={(e) => setHeaderLine(i, "spacing", parseFloat(e.target.value) || 1)} data-testid={`cs-header-spacing-${i}`} />
                      <button type="button" onClick={() => setHeaderLine(i, "bold", !l.bold)} title="Heading / Bold" className={`h-8 w-8 rounded border text-sm font-bold ${l.bold ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "border-slate-200 text-slate-500"}`} data-testid={`cs-header-bold-${i}`}>H</button>
                      <button type="button" onClick={() => removeHeaderLine(i)} className="h-8 w-8 rounded text-rose-500 hover:bg-rose-50 flex items-center justify-center" data-testid={`cs-header-remove-${i}`}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Title</Label>
                  <Input value={cs.title || ""} onChange={(e) => setCsField("title", e.target.value)} data-testid="cs-title" />
                </div>
                <div>
                  <Label>Copy Type</Label>
                  <Select value={cs.copy_type || "none"} onValueChange={(v) => setCsField("copy_type", v)}>
                    <SelectTrigger data-testid="cs-copy-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Certificate Type</SelectItem>
                      <SelectItem value="original">Original Copy</SelectItem>
                      <SelectItem value="duplicate">Duplicate Copy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-1 block">Body</Label>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div>
                    <Label>Se. No.</Label>
                    <Select value={cs.serial_show || "hide"} onValueChange={(v) => setCsField("serial_show", v)}>
                      <SelectTrigger data-testid="cs-serial-show"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hide">Hide</SelectItem>
                        <SelectItem value="show">Show</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {cs.serial_show === "show" && (
                    <>
                      <div>
                        <Label>Se. No. Type</Label>
                        <Select value={cs.serial_type || "automatic"} onValueChange={(v) => setCsField("serial_type", v)}>
                          <SelectTrigger data-testid="cs-serial-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="automatic">Automatic</SelectItem>
                            <SelectItem value="manual">Manual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Se. No. Label</Label><Input value={cs.serial_label ?? "Se. No."} onChange={(e) => setCsField("serial_label", e.target.value)} data-testid="cs-serial-label" /></div>
                      {cs.serial_type === "automatic" && (
                        <div><Label>No. from Start</Label><Input type="number" min="1" value={cs.serial_start ?? 1} onChange={(e) => setCsField("serial_start", parseInt(e.target.value) || 1)} data-testid="cs-serial-start" /></div>
                      )}
                    </>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 p-2 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="mb-0 text-indigo-600">Upper Section</Label>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => addTextLine("upper")} data-testid="cs-upper-add-text"><Plus size={13} />Text Field</Button>
                      <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => addFieldLine("upper")} data-testid="cs-upper-add-field"><Plus size={13} />Student Field</Button>
                    </div>
                  </div>
                  <div className={`space-y-2 ${rz}`}>
                    {(cs.body_lines || []).map((l, i) => ((l.section || "lower") === "upper" ? renderBodyRow(l, i) : null))}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 p-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="mb-0 text-indigo-600">Lower Section</Label>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => addTextLine("lower")} data-testid="cs-lower-add-text"><Plus size={13} />Text Field</Button>
                      <Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => addFieldLine("lower")} data-testid="cs-lower-add-field"><Plus size={13} />Student Field</Button>
                    </div>
                  </div>
                  <div className={`space-y-2 ${rz}`}>
                    {(cs.body_lines || []).map((l, i) => ((l.section || "lower") !== "upper" ? renderBodyRow(l, i) : null))}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Fields: {PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Alignment</Label>
                  <Select value={cs.align || "left"} onValueChange={(v) => setCsField("align", v)}>
                    <SelectTrigger data-testid="cs-align"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Title Size</Label><Input type="number" min="10" max="30" value={cs.title_size} onChange={(e) => setCsField("title_size", parseFloat(e.target.value) || 16)} data-testid="cs-title-size" /></div>
                  <div><Label>Body Size</Label><Input type="number" min="8" max="20" value={cs.body_size} onChange={(e) => setCsField("body_size", parseFloat(e.target.value) || 12)} data-testid="cs-body-size" /></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Signatory</Label><Input value={cs.signatory || ""} onChange={(e) => setCsField("signatory", e.target.value)} data-testid="cs-signatory" /></div>
                <div><Label>Date Label</Label><Input value={cs.date_label || ""} onChange={(e) => setCsField("date_label", e.target.value)} data-testid="cs-date-label" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Title Font</Label>
                  <select className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 bg-white" value={cs.title_font || "Helvetica"} onChange={(e) => setCsField("title_font", e.target.value)} data-testid="cs-title-font">
                    {FONTS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Body Font</Label>
                  <select className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 bg-white" value={cs.body_font || "Helvetica"} onChange={(e) => setCsField("body_font", e.target.value)} data-testid="cs-body-font">
                    {FONTS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                  </select>
                </div>
                <div><Label>Line Spacing</Label><Input type="number" step="0.1" min="0.5" value={cs.line_spacing ?? 1} onChange={(e) => setCsField("line_spacing", parseFloat(e.target.value) || 1)} data-testid="cs-line-spacing" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {[["show_header", "School Header"], ["show_border", "Border"], ["show_date", "Date"], ["show_signatory", "Signatory"], ["auto_arrange", "Auto Arrange (fit & balance)"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg border border-slate-100">
                    <span className="text-sm text-slate-700">{lbl}</span>
                    <Switch checked={cs[k] !== false} onCheckedChange={(v) => setCsField(k, v)} data-testid={`cs-toggle-${k}`} />
                  </label>
                ))}
              </div>
            </div>
            {/* Preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0 block">Preview <span className="text-slate-400 font-normal">(A4 {cs.orientation === "landscape" ? "Landscape" : "Portrait"})</span></Label>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-2" data-testid="cs-margins">
                {[["margin_top", "Top"], ["margin_right", "Right"], ["margin_bottom", "Bottom"], ["margin_left", "Left"]].map(([k, lbl]) => (
                  <div key={k}>
                    <Label className="text-[11px] text-slate-500">{lbl} (mm)</Label>
                    <Input type="number" min="0" max="60" className="h-8" value={cs[k] ?? (k === "margin_top" || k === "margin_bottom" ? 20 : 30)} onChange={(e) => setCsField(k, parseFloat(e.target.value) || 0)} data-testid={`cs-${k}`} />
                  </div>
                ))}
              </div>
              <div className={`bg-slate-100 rounded-lg p-4 flex justify-center overflow-auto ${rz}`} style={{ maxHeight: "70vh" }}>
                <div className="relative shrink-0" style={{ width: (cs.orientation === "landscape" ? 540 : 382) * previewScale, height: (cs.orientation === "landscape" ? 382 : 540) * previewScale }}>
                  <iframe title="Certificate Preview" src={prevUrl} className="w-full h-full border border-slate-300 bg-white shadow-lg" data-testid="cs-preview" />
                  {prevLoading && <div className="absolute inset-0 flex items-center justify-center bg-white/60 pointer-events-none"><Loader2 className="animate-spin text-indigo-500" size={22} /></div>}
                  {!prevUrl && !prevLoading && <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm pointer-events-none">Preview loads here…</div>}
                </div>
                {false && (
                <div className="hidden">
                  {cs.watermark && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" data-testid="cs-preview-watermark">
                      {cs.watermark_type === "text" ? (
                        <span style={{ transform: "rotate(-35deg)", opacity: cs.watermark_opacity ?? 0.08, fontSize: (cs.watermark_size || 120) * 0.42 + "px", fontWeight: "bold", whiteSpace: "nowrap", color: "#334155" }}>
                          {cs.watermark_text || school?.school_name || "SSSPS"}
                        </span>
                      ) : school?.logo ? (
                        <img src={school.logo} alt="watermark" style={{ opacity: cs.watermark_opacity ?? 0.08, width: (cs.watermark_size || 120) + "px", height: (cs.watermark_size || 120) + "px", objectFit: "contain" }} />
                      ) : (
                        <span className="text-slate-300 text-xs">No logo uploaded</span>
                      )}
                    </div>
                  )}
                  <div className="relative z-10 text-center">
                    {(() => {
                      const sideBySide = cs.show_logo && school?.logo && (cs.logo_align === "left" || cs.logo_align === "right");
                      const logoEl = cs.show_logo && school?.logo ? (
                        <img src={school.logo} alt="logo" className="object-contain block shrink-0" style={{ width: (cs.logo_size || 22) * 2 + "px", height: (cs.logo_size || 22) * 2 + "px" }} data-testid="cs-preview-logo" />
                      ) : null;
                      const headerEl = cs.show_header !== false ? (
                        <div className={sideBySide ? "flex-1 min-w-0" : ""}>
                          {(cs.header_lines || []).map((l, i) => (
                            <div key={i} style={{ textAlign: l.align, fontSize: (l.size || 10) + "px", fontWeight: l.bold ? "bold" : "normal", fontFamily: FONT_CSS[l.font] || "sans-serif", lineHeight: (l.spacing || 1) * 1.2 }}>{l.text}</div>
                          ))}
                        </div>
                      ) : null;
                      if (sideBySide) {
                        return <div className="flex items-center gap-3 mb-3" style={{ flexDirection: cs.logo_align === "right" ? "row-reverse" : "row" }}>{logoEl}{headerEl}</div>;
                      }
                      return (
                        <>
                          {logoEl && <div className="mb-1 flex justify-center" style={{ lineHeight: 0 }}>{logoEl}</div>}
                          {headerEl && <div className="mb-3">{headerEl.props.children}</div>}
                        </>
                      );
                    })()}
                    <div className="relative my-3">
                      <div className="font-bold" style={{ fontSize: (cs.title_size || 16) + "px", fontFamily: FONT_CSS[cs.title_font] || "sans-serif" }}>{fillTpl(cs.title)}</div>
                      {(cs.copy_type === "original" || cs.copy_type === "duplicate") && (
                        <span className="absolute right-0 top-1/2 -translate-y-1/2 italic text-[11px] text-slate-500" data-testid="cs-preview-copy">({cs.copy_type === "duplicate" ? "Duplicate Copy" : "Original Copy"})</span>
                      )}
                    </div>
                    {(() => {
                      const bls = cs.body_lines || [];
                      const rightIdx = (bls[0] && bls[0].flow === "same") ? 0 : bls.findIndex((l) => l.flow === "serial");
                      const rightLine = rightIdx >= 0 ? bls[rightIdx] : null;
                      const showSerial = cs.serial_show === "show";
                      if (!showSerial && !rightLine) return null;
                      const rightText = rightLine ? (rightLine.kind === "field" ? ((rightLine.label ? rightLine.label + ": " : "") + fillTpl(rightLine.value)) : (fillTpl(rightLine.text) || "")) : "";
                      return (
                        <div className="flex justify-between" style={{ fontSize: (cs.body_size || 12) + "px", fontFamily: FONT_CSS[cs.body_font] || "sans-serif" }} data-testid="cs-preview-serial">
                          <span>{showSerial ? `${cs.serial_label ?? "Se. No."}: ${cs.serial_type === "manual" ? "____" : (cs.serial_start ?? 1)}` : ""}</span>
                          <span>{rightText}</span>
                        </div>
                      );
                    })()}
                    {(cs.body_lines && cs.body_lines.length) ? (() => {
                      const bls = cs.body_lines;
                      const rightIdx = (bls[0] && bls[0].flow === "same") ? 0 : bls.findIndex((l) => l.flow === "serial");
                      const pieceStyle = (l) => ({ fontSize: (l.size || cs.body_size || 12) + "px", fontWeight: l.bold ? "bold" : "normal", fontFamily: FONT_CSS[l.font || cs.body_font] || "sans-serif" });
                      const renderGroups = (entries, kp) => {
                        const groups = [];
                        entries.forEach(({ l, i }) => {
                          if (l.flow === "same" && groups.length) groups[groups.length - 1].push({ l, i });
                          else groups.push([{ l, i }]);
                        });
                        return groups.map((grp, gi) => {
                          if (grp.length === 1) {
                            const { l } = grp[0];
                            return l.kind === "field" ? (
                              <div key={kp + gi} className="flex" style={{ ...pieceStyle(l), lineHeight: (l.spacing || 1) * 1.4 }}>
                                <span className="text-left" style={{ width: "42%" }}>{l.label}</span>
                                <span className="text-left flex-1">{fillTpl(l.value)}</span>
                              </div>
                            ) : (
                              <div key={kp + gi} style={{ textAlign: l.align || cs.align || "left", ...pieceStyle(l), lineHeight: (l.spacing || 1) * 1.4 }}>{fillTpl(l.text) || "\u00A0"}</div>
                            );
                          }
                          const first = grp[0].l;
                          return (
                            <div key={kp + gi} className="flex w-full gap-x-3" style={{ justifyContent: "space-between", lineHeight: (first.spacing || 1) * 1.4 }}>
                              {grp.map(({ l, i }) => (
                                <span key={i} style={pieceStyle(l)}>{l.kind === "field" ? ((l.label ? l.label + ": " : "") + fillTpl(l.value)) : (fillTpl(l.text) || "\u00A0")}</span>
                              ))}
                            </div>
                          );
                        });
                      };
                      const upper = bls.map((l, i) => ({ l, i })).filter(({ l, i }) => i !== rightIdx && l.flow !== "serial" && (l.section || "lower") === "upper");
                      const lower = bls.map((l, i) => ({ l, i })).filter(({ l, i }) => i !== rightIdx && l.flow !== "serial" && (l.section || "lower") !== "upper");
                      const lowerTable = lower.length ? (
                        <table className="w-full table-auto border-collapse" data-testid="cs-preview-lower">
                          <tbody>
                            {lower.map(({ l }, idx) => (
                              <tr key={idx} style={{ ...pieceStyle(l), lineHeight: (l.spacing || 1) * 1.4 }}>
                                <td className="pr-2 align-top whitespace-nowrap text-left">{idx + 1}.</td>
                                {l.kind === "field" ? (
                                  <>
                                    <td className="pr-3 align-top whitespace-nowrap text-left">{l.label}</td>
                                    <td className="align-top text-left w-full">{fillTpl(l.value)}</td>
                                  </>
                                ) : (
                                  <td className="align-top text-left" colSpan={2}>{fillTpl(l.text)}</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : null;
                      return (<>{renderGroups(upper, "u")}{upper.length > 0 && lower.length > 0 && <div className="mt-4" />}{lowerTable}</>);
                    })() : (
                      <div className="whitespace-pre-wrap" style={{ textAlign: cs.align || "left", fontSize: (cs.body_size || 12) + "px", fontFamily: FONT_CSS[cs.body_font] || "sans-serif", lineHeight: (cs.line_spacing || 1) * 1.5 }}>{fillTpl(cs.body)}</div>
                    )}
                    <div className="flex justify-between mt-8 text-[10px]">
                      <span>{cs.show_date !== false ? `${cs.date_label || "Date:"} __________` : ""}</span>
                      <span>{cs.show_signatory !== false ? (cs.signatory || "Principal") : ""}</span>
                    </div>
                  </div>
                  <div onMouseDown={startResize} title="Drag to resize preview" className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-20" style={{ background: "linear-gradient(135deg, transparent 50%, #94a3b8 50%)" }} data-testid="cs-preview-resize" />
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Layout / Logo / Watermark controls */}
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="cs-layout-bar">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              {/* Orientation */}
              <div className="flex items-center gap-2">
                <Label className="mb-0 whitespace-nowrap">Orientation</Label>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
                  {["portrait", "landscape"].map((o) => (
                    <button key={o} type="button" onClick={() => setCsField("orientation", o)}
                      className={`px-3 h-8 text-xs font-medium capitalize ${cs.orientation === o ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                      data-testid={`cs-orientation-${o}`}>{o}</button>
                  ))}
                </div>
              </div>
              {/* Logo */}
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setCsField("show_logo", !cs.show_logo)}
                  className={`px-3 h-8 rounded-lg text-xs font-medium border ${cs.show_logo ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  data-testid="cs-logo-toggle">School Logo: {cs.show_logo ? "On" : "Off"}</button>
                {cs.show_logo && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500">Align</span>
                    <select className="h-8 rounded border border-slate-200 text-xs px-2 bg-white" value={cs.logo_align || "center"} onChange={(e) => setCsField("logo_align", e.target.value)} data-testid="cs-logo-align">
                      <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                    </select>
                    <span className="text-xs text-slate-500">Size</span>
                    <Input type="number" min="8" max="60" className="w-16 h-8" value={cs.logo_size} onChange={(e) => setCsField("logo_size", parseFloat(e.target.value) || 22)} data-testid="cs-logo-size" />
                    <span className="text-xs text-slate-400">mm</span>
                  </div>
                )}
              </div>
              {/* Watermark */}
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setCsField("watermark", !cs.watermark)}
                  className={`px-3 h-8 rounded-lg text-xs font-medium border ${cs.watermark ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  data-testid="cs-watermark-toggle">Watermark: {cs.watermark ? "On" : "Off"}</button>
                {cs.watermark && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select className="h-8 rounded border border-slate-200 text-xs px-2 bg-white" value={cs.watermark_type || "logo"} onChange={(e) => setCsField("watermark_type", e.target.value)} data-testid="cs-watermark-type">
                      <option value="logo">Logo</option>
                      <option value="text">Text</option>
                    </select>
                    {cs.watermark_type === "text" && (
                      <Input className="w-44 h-8" placeholder={school?.school_name || "Watermark text"} value={cs.watermark_text || ""} onChange={(e) => setCsField("watermark_text", e.target.value)} data-testid="cs-watermark-text" />
                    )}
                    <span className="text-xs text-slate-500">Size</span>
                    <Input type="number" min="40" max="300" className="w-16 h-8" value={cs.watermark_size} onChange={(e) => setCsField("watermark_size", parseFloat(e.target.value) || 120)} data-testid="cs-watermark-size" />
                    <span className="text-xs text-slate-500">Opacity</span>
                    <Input type="number" step="0.02" min="0.02" max="0.5" className="w-16 h-8" value={cs.watermark_opacity} onChange={(e) => setCsField("watermark_opacity", parseFloat(e.target.value) || 0.08)} data-testid="cs-watermark-opacity" />
                  </div>
                )}
              </div>
            </div>
            {ctype === "transfer" && (
              <div className="flex items-center gap-2 pt-3 border-t border-slate-200" data-testid="cs-allow-due-row">
                <Switch checked={cs.allow_due_action === true} onCheckedChange={(v) => setCsField("allow_due_action", v)} data-testid="cs-allow-due-action" />
                <Label className="mb-0">Enable action buttons even when fee is due</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingCs} className="gap-1.5" data-testid="cs-save-btn">{savingCs ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fmtTarget} onOpenChange={(o) => !o && setFmtTarget(null)}>        <DialogContent className="max-w-sm" data-testid="tc-format-dialog">
          <DialogHeader>
            <DialogTitle>Download Transfer Certificate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Choose a file format for <b>{fmtTarget?.name}</b>'s certificate.</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="flex-1 gap-1.5" onClick={() => { const s = fmtTarget; setFmtTarget(null); doGenerate(s, false, "pdf"); }} data-testid="tc-format-pdf">
              <FileText size={15} />PDF
            </Button>
            <Button className="flex-1 gap-1.5" onClick={() => { const s = fmtTarget; setFmtTarget(null); doGenerate(s, false, "docx"); }} data-testid="tc-format-word">
              <FileText size={15} />Word (.docx)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UseTemplateDialog
        open={tplOpen}
        onOpenChange={setTplOpen}
        categories={["Bonafide", "Certificate", "Character Certificate", "Transfer Certificate", "Marksheet", "Academic", "General", "Admit Card"]}
        heading={`Use a Ready Template — ${meta.label}`}
        getPayload={() => ({ student_ids: (selected.size ? filtered.filter((s) => selected.has(s.id)).map((s) => s.id) : filtered.map((s) => s.id)) })}
      />
    </div>
  );
}
